/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import CustomButton from '@/components/common/Button';
import {
  Box,
  Button,
  Flex,
  Text,
  VStack,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useDisclosure,
  TabIndicator,
  HStack,
  Image,
  Grid,
  GridItem,
  Card,
  SkeletonCircle,
  SkeletonText,
  Link,
} from '@chakra-ui/react';
import {
  useDeleteAccount,
  useGetAccountDetails,
  useUpdateAccountDetails,
  useGetCountries,
  useGetGenders,
} from '@/services/account-setting';
import { useUpdateUser } from '@/services/auth/auth';
import * as yup from 'yup';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import useCustomToast from '@/components/common/Toast';
import { AxiosError } from 'axios';
import { useLocation, useNavigate } from 'react-router-dom';
import { NAVIGATION_ROUTES } from '@/routes/constants';
import TokenService from '@/services/service.token';
import withAuth from './../../HOCs/withAuth';
import ChangePassword from './ChangePassword';
import EditModal from './EditModal';
import AccountEditModal from './AccountEditModal';
import StoreSettings from './StoreSettings';
import DeleteAccountModal from './DeleteAccountModal';
import { useStore } from '@/store/store';
import { useQueryClient } from 'react-query';
import { formatApiDateToDisplay } from '@/utils/formatDateString';

const validationSchema = yup.object().shape({
  name: yup.string().required('Shop name is required'),
  email: yup.string().email('Invalid email').required('Email is required'),
  telephone: yup.string().required('Business contact is required'),
  company_address: yup.string().optional(),
  certificate_image: yup.mixed().nullable().optional(),
  pan_vat_certificate_image: yup.mixed().nullable().optional(),
}) as yup.ObjectSchema<VendorFormData>;

interface VendorFormData {
  name: string;
  email: string;
  telephone: string;
  company_address?: string;
  certificate_image?: File | string | null;
  pan_vat_certificate_image?: File | string | null;
}

interface AccountDetailsProps {
  user: any;
  isLoading: boolean;
  onEdit: () => void;
  formatLastLogin: (timestamp: string) => string;
  handleFileClick: (fileUrl: string | null) => void;
}

const AccountDetails: React.FC<AccountDetailsProps> = React.memo(
  ({ user, isLoading, onEdit, handleFileClick }) => (
    <Card
      shadow={'none'}
      rounded={'lg'}
      bg={'background.white'}
      borderColor={'border.default60'}
      borderWidth="1px"
    >
      <Flex p={4} bg={'background.tertiary'} justify="space-between" align="center">
        <Text fontSize="lg" fontWeight={'semibold'}>
          Account Details
        </Text>
        <CustomButton rounded={'lg'} px={8} fontSize={'sm'} colorScheme="red" onClick={onEdit}>
          Edit
        </CustomButton>
      </Flex>
      <HStack
        justify={'start'}
        p={4}
        bg={'background.white'}
        spacing={4}
        align="stretch"
        css={{
          overflowX: 'auto',
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
        }}
      >
        <Grid
          templateColumns={{
            base: 'repeat(2, 1fr)',
            md: 'repeat(4, 1fr)',
          }}
          gap={4}
          w="full"
          h="full"
        >
          <GridItem>
            <VStack align="start" gap={1} h="full">
              <Text fontSize={'sm'} color={'text.secondary'}>
                Profile Photo:
              </Text>
              {isLoading ? (
                <SkeletonCircle bg={'button.disable'} size="60px" />
              ) : user?.photo_small ? (
                <HStack spacing={2} align="center">
                  <Image
                    src={user.photo_small}
                    alt="Profile Photo"
                    boxSize="60px"
                    objectFit="cover"
                    borderRadius={'md'}
                    cursor="pointer"
                    _hover={{ opacity: 0.8 }}
                    onClick={() => handleFileClick(user.photo)}
                  />
                </HStack>
              ) : (
                <Text fontSize={'sm'} color={'text.secondary'}>
                  -
                </Text>
              )}
            </VStack>
          </GridItem>
          <GridItem>
            <VStack align="start" gap={1} h="full">
              <Text fontSize={'sm'} color={'text.secondary'}>
                Full Name:
              </Text>
              {isLoading ? (
                <SkeletonText bg={'button.disable'} noOfLines={1} width="150px" />
              ) : (
                <Text>
                  {(user?.first_name || '') + (user?.last_name ? ' ' + user.last_name : '') || '-'}
                </Text>
              )}
            </VStack>
          </GridItem>
          <GridItem>
            <VStack align="start" gap={1} h="full">
              <Text fontSize={'sm'} color={'text.secondary'}>
                Email:
              </Text>
              {isLoading ? (
                <SkeletonText bg={'button.disable'} noOfLines={1} width="150px" />
              ) : (
                <Text>{user?.email || '-'}</Text>
              )}
            </VStack>
          </GridItem>
          <GridItem>
            <VStack align="start" gap={1} h="full">
              <Text fontSize={'sm'} color={'text.secondary'}>
                Phone:
              </Text>
              {isLoading ? (
                <SkeletonText bg={'button.disable'} noOfLines={1} width="150px" />
              ) : (
                <Text>{user?.phone || '-'}</Text>
              )}
            </VStack>
          </GridItem>
          <GridItem>
            <VStack align="start" gap={1} h="full">
              <Text fontSize={'sm'} color={'text.secondary'}>
                Gender:
              </Text>
              {isLoading ? (
                <SkeletonText bg={'button.disable'} noOfLines={1} width="150px" />
              ) : (
                <Text>{user?.gender || '-'}</Text>
              )}
            </VStack>
          </GridItem>
          <GridItem>
            <VStack align="start" gap={1} h="full">
              <Text fontSize={'sm'} color={'text.secondary'}>
                Country:
              </Text>
              {isLoading ? (
                <SkeletonText bg={'button.disable'} noOfLines={1} width="150px" />
              ) : (
                <Text>{user?.country || '-'}</Text>
              )}
            </VStack>
          </GridItem>
          <GridItem>
            <VStack align="start" gap={1} h="full">
              <Text fontSize={'sm'} color={'text.secondary'}>
                Address:
              </Text>
              {isLoading ? (
                <SkeletonText bg={'button.disable'} noOfLines={1} width="200px" />
              ) : (
                <Text>{user?.address || '-'}</Text>
              )}
            </VStack>
          </GridItem>
          <GridItem>
            <VStack align="start" gap={1} h="full">
              <Text fontSize={'sm'} color={'text.secondary'}>
                Currency:
              </Text>
              {isLoading ? (
                <SkeletonText bg={'button.disable'} noOfLines={1} width="200px" />
              ) : (
                <Text>{user?.currency || '-'}</Text>
              )}
            </VStack>
          </GridItem>
          <GridItem>
            <VStack align="start" gap={1} h="full">
              <Text fontSize={'sm'} color={'text.secondary'}>
                Last Login:
              </Text>
              {isLoading ? (
                <SkeletonText bg={'button.disable'} noOfLines={1} width="150px" />
              ) : (
                <Text>{user?.last_login ? formatApiDateToDisplay(user.last_login) : '-'} UTC</Text>
              )}
            </VStack>
          </GridItem>
        </Grid>
      </HStack>
    </Card>
  ),
);

interface VendorDetailsProps {
  isVendor: boolean;
  data: any;
  vendorLoading: boolean;
  vendorError: any;
  onEdit: () => void;
  handleFileClick: (fileUrl: string | null) => void;
  user: any;
}

const VendorDetails: React.FC<VendorDetailsProps> = React.memo(
  ({ isVendor, data, vendorLoading, vendorError, onEdit, handleFileClick }) => {
    if (!isVendor) return null;
    if (vendorLoading) {
      return (
        <Card
          shadow={'none'}
          rounded={'lg'}
          bg={'background.white'}
          borderColor={'border.default60'}
          borderWidth="1px"
        >
          <Flex p={4} bg={'background.tertiary'} justify="space-between" align="center">
            <Text fontSize="lg" fontWeight={'semibold'}>
              Vendor Details
            </Text>
            <CustomButton rounded={'lg'} px={8} fontSize={'sm'} colorScheme="red" onClick={onEdit}>
              Edit
            </CustomButton>
          </Flex>
          <HStack
            justify={'start'}
            p={4}
            bg={'background.white'}
            spacing={4}
            align="stretch"
            css={{
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              scrollbarWidth: 'none',
            }}
          >
            <Grid
              templateColumns={{
                base: 'repeat(2, 1fr)',
                md: 'repeat(4, 1fr)',
              }}
              gap={4}
              w="full"
              h="full"
            >
              <GridItem>
                <VStack align="start" gap={1} h="full">
                  <Text fontSize={'sm'} color={'text.secondary'}>
                    Company Certificate:
                  </Text>
                  <SkeletonCircle bg={'button.disable'} size="60px" />
                </VStack>
              </GridItem>
              <GridItem>
                <VStack align="start" gap={1} h="full">
                  <Text fontSize={'sm'} color={'text.secondary'}>
                    Pan/VAT Certificate:
                  </Text>
                  <SkeletonCircle bg={'button.disable'} size="60px" />
                </VStack>
              </GridItem>
              <GridItem>
                <VStack align="start" gap={1} h="full">
                  <Text fontSize={'sm'} color={'text.secondary'}>
                    Email Address:
                  </Text>
                  <SkeletonText bg={'button.disable'} noOfLines={1} width="150px" />
                </VStack>
              </GridItem>
              <GridItem>
                <VStack align="start" gap={1} h="full">
                  <Text fontSize={'sm'} color={'text.secondary'}>
                    Business Contact:
                  </Text>
                  <SkeletonText bg={'button.disable'} noOfLines={1} width="150px" />
                </VStack>
              </GridItem>
              <GridItem colSpan={{ base: 2, md: 2 }}>
                <VStack align="start" gap={1} h="full">
                  <Text fontSize={'sm'} color={'text.secondary'}>
                    Company Address:
                  </Text>
                  <SkeletonText bg={'button.disable'} noOfLines={1} width="200px" />
                </VStack>
              </GridItem>
              <GridItem>
                <VStack align="start" gap={1} h="full">
                  <Text fontSize={'sm'} color={'text.secondary'}>
                    Account Created:
                  </Text>
                  <SkeletonText bg={'button.disable'} noOfLines={1} width="150px" />
                </VStack>
              </GridItem>
            </Grid>
          </HStack>
        </Card>
      );
    }
    if (vendorError) {
      return (
        <Box p={4}>
          <Text color="status.error">Failed to load vendor data. Please try again later.</Text>
        </Box>
      );
    }

    return (
      <Card
        shadow={'none'}
        rounded={'lg'}
        bg={'background.white'}
        borderColor={'border.default60'}
        borderWidth="1px"
      >
        <Flex p={4} bg={'background.tertiary'} justify="space-between" align="center">
          <Text fontSize="lg" fontWeight={'semibold'}>
            Vendor Details
          </Text>
          <CustomButton rounded={'lg'} px={8} fontSize={'sm'} colorScheme="red" onClick={onEdit}>
            Edit
          </CustomButton>
        </Flex>
        <HStack
          justify={'start'}
          p={4}
          bg={'background.white'}
          spacing={4}
          align="stretch"
          css={{
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          <Grid
            templateColumns={{
              base: 'repeat(2, 1fr)',
              md: 'repeat(4, 1fr)',
            }}
            gap={4}
            w="full"
            h="full"
          >
            <GridItem>
              <VStack align="start" gap={1} h="full">
                <Text fontSize={'sm'} color={'text.secondary'}>
                  Company Certificate:
                </Text>
                {data?.certificate_image ? (
                  <HStack spacing={2} align="center">
                    <Image
                      src={data.certificate_image}
                      alt="Company Certificate"
                      boxSize="60px"
                      objectFit="cover"
                      borderRadius={'md'}
                      cursor="pointer"
                      _hover={{ opacity: 0.8 }}
                      onClick={() => handleFileClick(data.certificate_image)}
                    />
                  </HStack>
                ) : (
                  <Text fontSize={'sm'} color={'text.secondary'}>
                    -
                  </Text>
                )}
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" gap={1} h="full">
                <Text fontSize={'sm'} color={'text.secondary'}>
                  Pan/VAT Certificate:
                </Text>
                {data?.pan_vat_certificate_image ? (
                  <HStack spacing={2} align="center">
                    <Image
                      src={data.pan_vat_certificate_image}
                      alt="Pan/VAT Certificate"
                      boxSize="60px"
                      objectFit="cover"
                      borderRadius={'md'}
                      cursor="pointer"
                      _hover={{ opacity: 0.8 }}
                      onClick={() => handleFileClick(data.pan_vat_certificate_image)}
                    />
                  </HStack>
                ) : (
                  <Text fontSize={'sm'} color={'text.secondary'}>
                    -
                  </Text>
                )}
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" gap={1} h="full">
                <Text fontSize={'sm'} color={'text.secondary'}>
                  Email Address:
                </Text>
                <Text>{data?.email || '-'}</Text>
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" gap={1} h="full">
                <Text fontSize={'sm'} color={'text.secondary'}>
                  Business Contact:
                </Text>
                <Text>{data?.telephone || '-'}</Text>
              </VStack>
            </GridItem>
            <GridItem colSpan={{ base: 2, md: 2 }}>
              <VStack align="start" gap={1} h="full">
                <Text fontSize={'sm'} color={'text.secondary'}>
                  Company Address:
                </Text>
                <Text>{data?.company_address || '-'}</Text>
              </VStack>
            </GridItem>
            <GridItem>
              <VStack align="start" gap={1} h="full">
                <Text fontSize={'sm'} color={'text.secondary'}>
                  Account Created:
                </Text>
                <Text>
                  {data?.created_at ? new Date(data.created_at).toLocaleDateString() : '-'}
                </Text>
              </VStack>
            </GridItem>
          </Grid>
        </HStack>
      </Card>
    );
  },
);

interface DeleteAccountProps {
  isVendor: boolean;
  onDelete: () => void;
}

const DeleteAccount: React.FC<DeleteAccountProps> = React.memo(({ isVendor, onDelete }) => {
  if (!isVendor) return null;
  return (
    <Card
      shadow={'none'}
      rounded={'lg'}
      bg={'background.white'}
      borderColor={'border.default60'}
      borderWidth="1px"
    >
      <HStack p={4}>
        <VStack align={'start'} spacing={4}>
          <Flex justify="space-between" align="center" width="full">
            <Text fontSize="lg" fontWeight={'semibold'}>
              Delete Account
            </Text>
          </Flex>
          <Text>
            Permanently deleting your account will remove you from our platform, revoke access to
            your vendor account, and cannot be undone. You may re-register using the same email
            address, but you will need to complete the setup process again. Please proceed with
            caution.
          </Text>
        </VStack>
        <Button
          variant="outline"
          rounded={'lg'}
          px={10}
          fontSize={'sm'}
          colorScheme="brand.primary"
          _hover={{ bg: '#0000' }}
          onClick={onDelete}
        >
          Delete
        </Button>
      </HStack>
    </Card>
  );
});

const Settings: React.FC = () => {
  const { user, setUser } = useStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const isVendor = user?.role === 'vendor';
  const queryClient = useQueryClient();

  const { reset: vendorReset } = useForm<VendorFormData>({
    resolver: yupResolver(validationSchema),
    mode: 'onChange',
  });

  const { data, isLoading: vendorLoading, error: vendorError } = useGetAccountDetails();
  const { data: gendersData, isLoading: gendersLoading } = useGetGenders();
  const { data: countriesData, isLoading: countriesLoading } = useGetCountries();
  const { mutateAsync: updateAccountDetails } = useUpdateAccountDetails();
  const { mutateAsync: deleteAccount } = useDeleteAccount();
  const { mutateAsync: updateUserDetails } = useUpdateUser();

  const { isOpen: vendorModalOpen, onOpen: vendorOnOpen, onClose: vendorOnClose } = useDisclosure();
  const {
    isOpen: accountModalOpen,
    onOpen: accountOnOpen,
    onClose: accountOnClose,
  } = useDisclosure();
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const { showToast } = useCustomToast();

  const isLoading = !user || gendersLoading || countriesLoading;

  // In Settings.tsx
  const initialAccountData = useMemo(() => {
    if (!user || !gendersData || !countriesData) {
      return {
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        gender: '',
        country: '',
        address: '',
        photo: null,
      };
    }

    const genderId = user.gender
      ? gendersData.find((g: any) => g.gender_type === user.gender)?.id?.toString() || ''
      : '';
    const countryId = user.country
      ? (countriesData as any)?.results
          ?.find((c: any) => c.country_name === user.country)
          ?.id?.toString() || ''
      : '';

    return {
      first_name: user.first_name || '',
      last_name: user.last_name || '',
      email: user.email || '',
      phone: user.phone || '',
      gender: genderId,
      country: countryId,
      address: user.address || '',
      photo: null,
      photoUrl: user.photo || user.photo_small || undefined,
    };
  }, [user, gendersData, countriesData]);

  const [vendorData, setVendorData] = useState(data);

  useEffect(() => {
    setVendorData(data);
  }, [data]);

  const initialVendorData = useMemo(
    () =>
      vendorData
        ? {
            name: vendorData.shop_name || '',
            email: vendorData.email || '',
            telephone: vendorData.telephone || '',
            company_address: vendorData.company_address || '',
            certificate_image: null,
            pan_vat_certificate_image: null,
            certificate_image_url: vendorData.certificate_image || null,
            pan_vat_certificate_image_url: vendorData.pan_vat_certificate_image || null,
          }
        : {
            name: '',
            email: '',
            telephone: '',
            company_address: '',
            certificate_image: null,
            pan_vat_certificate_image: null,
            certificate_image_url: null,
            pan_vat_certificate_image_url: null,
          },
    [vendorData],
  );

  const getTabFromURL = useCallback(() => {
    const tabParam = searchParams.get('tab');
    if (tabParam === 'store-settings' && isVendor) return 1;
    return 0; // Default to account settings
  }, [searchParams, isVendor]);

  const [selectedTabIndex, setSelectedTabIndex] = useState(getTabFromURL);

  const handleTabChange = useCallback(
    (index: number) => {
      setSelectedTabIndex(index);

      const newSearchParams = new URLSearchParams(searchParams);
      if (index === 1 && isVendor) {
        newSearchParams.set('tab', 'store-settings');
      } else {
        newSearchParams.delete('tab');
      }
      setSearchParams(newSearchParams);
    },
    [searchParams, setSearchParams, isVendor],
  );

  useEffect(() => {
    setSelectedTabIndex(getTabFromURL());
  }, [searchParams, getTabFromURL]);

  const handleVendorEdit = useCallback(() => {
    if (isVendor) {
      vendorOnOpen();
    }
  }, [isVendor, vendorOnOpen]);

  const handleAccountEdit = useCallback(() => {
    accountOnOpen();
  }, [accountOnOpen]);

  const handleFileClick = useCallback((fileUrl: string | null) => {
    if (fileUrl) {
      window.open(fileUrl, '_blank');
    }
  }, []);

  const handleVendorUpdate = useCallback(
    async (data: VendorFormData) => {
      if (!isVendor) return;

      const formData = new FormData();
      formData.append('shop_name', data.name);
      formData.append('email', data.email);
      formData.append('telephone', data.telephone);
      if (data.company_address) {
        formData.append('company_address', data.company_address);
      }
      if (data.certificate_image && data.certificate_image instanceof File) {
        formData.append('certificate_image', data.certificate_image);
      }
      if (data.pan_vat_certificate_image && data.pan_vat_certificate_image instanceof File) {
        formData.append('pan_vat_certificate_image', data.pan_vat_certificate_image);
      }

      try {
        const response = await updateAccountDetails(formData);
        setVendorData(response);
        vendorOnClose();
        showToast('success', 'Vendor details updated successfully');
        queryClient.invalidateQueries('account-details');
      } catch (error) {
        if (error instanceof AxiosError) {
          showToast('error', error.response?.data?.error || 'Failed to update vendor details');
        }
      }
    },
    [isVendor, updateAccountDetails, vendorOnClose, showToast, queryClient],
  );

  const cleanValidationErrors = (errorMessage: string): string => {
    const sentences = errorMessage.split('.');
    const uniqueSentences = [];
    const seenSentences = new Set();

    for (const sentence of sentences) {
      const cleanSentence = sentence.trim();
      if (cleanSentence && !seenSentences.has(cleanSentence.toLowerCase())) {
        seenSentences.add(cleanSentence.toLowerCase());
        uniqueSentences.push(cleanSentence);
      }
    }

    return uniqueSentences.join('. ').trim();
  };

  const formatValidationError = (error: any): string => {
    console.error('API Error:', error); // ← Critical for debugging

    const data = error.response.data;

    // 1. String message
    if (typeof data === 'string') {
      return cleanValidationErrors(data);
    }

    // 2. { message: "..." }
    if (data.message) {
      return cleanValidationErrors(data.message);
    }

    // 3. { error: "..." }
    if (data.error) {
      return cleanValidationErrors(data.error);
    }

    // 4. { detail: "..." } — Django REST Framework style
    if (data.detail) {
      return cleanValidationErrors(data.detail);
    }

    // 5. Array of strings
    if (Array.isArray(data)) {
      return cleanValidationErrors(data.join('. '));
    }

    // 6. { non_field_errors: [...] }
    if (data.non_field_errors) {
      return cleanValidationErrors(data.non_field_errors.join('. '));
    }

    // 7. Field-specific errors: { field: ["msg"] }
    const fieldErrors: string[] = [];
    for (const [field, messages] of Object.entries(data)) {
      if (Array.isArray(messages)) {
        fieldErrors.push(`${field}: ${messages.join(', ')}`);
      } else if (typeof messages === 'string') {
        fieldErrors.push(`${field}: ${messages}`);
      }
    }

    if (fieldErrors.length > 0) {
      return fieldErrors.join('; ');
    }

    // Final fallback
    return 'An unexpected error occurred. Please try again.';
  };

  const handleAccountUpdate = useCallback(
    async (data: any) => {
      try {
        const response = await updateUserDetails(data);

        const updatedUser = {
          ...user!,
          ...data,
          gender:
            data.gender !== undefined
              ? gendersData?.find((g: any) => g.gender_type === data.gender)?.id || user!.gender
              : user!.gender,
          country:
            data.country !== undefined
              ? (countriesData as any)?.results?.find((c: any) => c.country_name === data.country)
                  ?.id || user!.country
              : user!.country,
          photo: response?.user?.photo || user!.photo, // safer: response has .user
          photo_small: response?.user?.photo_small || user!.photo_small,
        };

        setUser(updatedUser);
        accountOnClose();
        showToast('success', 'Account details updated successfully');
      } catch (error) {
        const errorMessage = formatValidationError(error);
        showToast('error', errorMessage);
        // Do NOT update user state here
      }
    },
    [updateUserDetails, user, gendersData, countriesData, setUser, accountOnClose, showToast],
  );

  const handleDelete = useCallback(() => {
    if (!isVendor) return;
    onDeleteOpen();
  }, [isVendor, onDeleteOpen]);

  const handleDeleteConfirm = useCallback(async () => {
    if (!isVendor) return;

    try {
      await deleteAccount();
      navigate(NAVIGATION_ROUTES.LOGIN);
      showToast('success', 'Account deleted successfully');
      TokenService.clearToken();
    } catch (error) {
      if (error instanceof AxiosError) {
        showToast('error', error.response?.data?.message || 'Failed to delete account');
      }
    }
  }, [isVendor, deleteAccount, navigate, showToast]);

  const handleVendorCancel = useCallback(() => {
    if (!isVendor) return;
    vendorReset();
    vendorOnClose();
  }, [isVendor, vendorReset, vendorOnClose]);

  const handleAccountCancel = useCallback(() => {
    accountOnClose();
  }, [accountOnClose]);

  const formatLastLogin = useCallback((timestamp: string) => {
    const date = new Date(timestamp);
    return (
      date.toLocaleDateString() +
      ' at ' +
      date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    );
  }, []);

  const { state } = useLocation();

  useEffect(() => {
    if (state?.tabIndex !== undefined) {
      if (state.tabIndex === 1 && !isVendor) {
        setSelectedTabIndex(0);
      } else {
        setSelectedTabIndex(state.tabIndex);
      }
    }
  }, [state, isVendor]);

  return (
    <Box fontFamily={'primary'}>
      <Tabs
        index={selectedTabIndex}
        onChange={handleTabChange}
        pt={4}
        colorScheme="brand.primary"
        color={'text.secondary'}
      >
        <TabList px={6} gap={8}>
          <Tab width={'200px'} px={5} fontWeight={'semibold'}>
            Account Settings
          </Tab>
          {isVendor && (
            <Tab width={'200px'} px={5} fontWeight={'semibold'}>
              Store Settings
            </Tab>
          )}
        </TabList>
        <TabIndicator mt="-1.5px" height="2px" bg="brand.primary.DEFAULT" borderRadius="1px" />
        <TabPanels
          px={6}
          css={{
            overflowX: 'auto',
            '&::-webkit-scrollbar': { display: 'none' },
            scrollbarWidth: 'none',
          }}
        >
          <TabPanel px={0}>
            <VStack spacing={6} align="stretch">
              <AccountDetails
                user={user}
                isLoading={isLoading}
                onEdit={handleAccountEdit}
                formatLastLogin={formatLastLogin}
                handleFileClick={handleFileClick}
              />
              {isVendor && <ChangePassword />}
              <DeleteAccount isVendor={isVendor} onDelete={handleDelete} />
              <Card
                p={4}
                bg="background.white"
                borderWidth="1px"
                borderColor="border.default60"
                rounded="lg"
                shadow={'none'}
              >
                <Text fontSize="sm" color="text.secondary">
                  For support, please call:
                  <Link href="https://wa.me/17202097941" isExternal>
                    <Text as="span" color={'text.primary'} fontWeight={'semibold'}>
                      {' '}
                      +1 (720) 209-7933 <span style={{ fontStyle: 'italic' }}>(Whatsapp)</span>
                    </Text>
                  </Link>
                </Text>
              </Card>
            </VStack>
          </TabPanel>
          {isVendor && (
            <TabPanel px={0}>
              <VStack spacing={4} align="stretch">
                <StoreSettings selectedTabIndex={selectedTabIndex} />
                <VendorDetails
                  isVendor={isVendor}
                  data={vendorData}
                  vendorLoading={vendorLoading}
                  vendorError={vendorError}
                  onEdit={handleVendorEdit}
                  handleFileClick={handleFileClick}
                  user={user}
                />
              </VStack>
            </TabPanel>
          )}
        </TabPanels>
      </Tabs>
      {isVendor && (
        <>
          <DeleteAccountModal
            isOpen={isDeleteOpen}
            onClose={onDeleteClose}
            onConfirm={handleDeleteConfirm}
          />
          <EditModal
            isOpen={vendorModalOpen}
            onClose={vendorOnClose}
            handleCancel={handleVendorCancel}
            handleUpdate={handleVendorUpdate}
            initialData={initialVendorData}
          />
        </>
      )}
      <AccountEditModal
        isOpen={accountModalOpen}
        onClose={accountOnClose}
        handleCancel={handleAccountCancel}
        handleUpdate={handleAccountUpdate}
        initialData={initialAccountData}
      />
    </Box>
  );
};

export default withAuth(Settings);
